/**
 * The enquiry list, from the server.
 *
 * The search box and the page are the server's — `GET /api/v1/inquiries/`
 * takes both — so typing or turning a page refetches rather than narrowing
 * what is already on screen: the list is paginated, and filtering a page in
 * the browser would only ever search the rows that page happened to hold.
 *
 * Laid over what comes back is whatever this sitting has done that the backend
 * has not been told about — a stage moved here, which would otherwise read as
 * the stage the row was fetched at. See `enquiriesService`.
 *
 * Which page is the caller's to hold, not this hook's: it is the thing a
 * screen has to reset when the search changes, and the hook has no way of
 * knowing that a keystroke came from the user rather than from a re-render.
 */

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import {
  DEFAULT_PAGE_SIZE,
  fetchEnquiriesPage,
  getEnquiryOverrides,
  subscribeToEnquiries,
} from "../services/enquiriesService";
import type { Enquiry } from "../types/enquiries.types";

/** Long enough that typing a name is one request, not eight. */
const SEARCH_DEBOUNCE_MS = 300;

export type EnquiriesStatus = "loading" | "ready" | "failed";

export interface EnquiriesState {
  /** One page of them — `pageSize` rows at most, not the whole list. */
  enquiries: Enquiry[];
  /** How many the server holds for this search, not how many came back. */
  count: number;
  status: EnquiriesStatus;
  /** Set only when `status` is `failed` — what the screen shows. */
  error?: string;
  /** Runs the call again. For a Try again button, not for every keystroke. */
  reload: () => void;
}

export interface UseEnquiriesOptions {
  /** Debounced here, so a call site can pass the box's value directly. */
  search?: string;
  /** 1-based, the way the endpoint counts. Changing it refetches. */
  page?: number;
  /** How many rows a page holds. Defaults to the service's ten. */
  pageSize?: number;
  /**
   * A stage to leave out — the endpoint's `notinclude`. Part of the cache key
   * below, so a list asked for without the converted ones is a different
   * question from the whole pipeline rather than the same one answered twice.
   */
  notInclude?: string;
}

const failureMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

const NO_ENQUIRIES: Enquiry[] = [];

export function useEnquiries({
  search = "",
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  notInclude,
}: UseEnquiriesOptions = {}): EnquiriesState {
  const getToken = useTokenGetter();

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const isFirstSearch = useRef(true);

  useEffect(() => {
    // The first pass must not wait — the screen would otherwise open on a
    // blank list for no reason, since there is nothing typed to debounce.
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      setDebouncedSearch(search);
      return;
    }

    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const query = useQuery({
    queryKey: [
      "enquiries",
      "list",
      debouncedSearch,
      page,
      pageSize,
      notInclude ?? "",
    ] as const,
    queryFn: ({ signal }) =>
      fetchEnquiriesPage(getToken, {
        search: debouncedSearch,
        page,
        pageSize,
        notInclude,
        signal,
      }),
    // The rows already on screen stay while the next set is fetched — cached
    // across mounts too, so a list visited again shows its last page instantly
    // while a fresh read runs behind it.
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (query.isError) console.error("Enquiries failed to load:", query.error);
  }, [query.isError, query.error]);

  const overrides = useSyncExternalStore(
    subscribeToEnquiries,
    getEnquiryOverrides,
    getEnquiryOverrides
  );

  // Back to nothing on a real failure, same as before: a row from a page that
  // is no longer confirmed current is worse than none.
  const fetched = query.isError ? NO_ENQUIRIES : query.data?.enquiries ?? NO_ENQUIRIES;

  // The payment comes over with the stage: a request answered from the table is
  // settled on the server, but the row it was answered from was fetched before
  // that happened, and would otherwise go on offering a tick and a cross
  // against a payment that is already in.
  const enquiries = useMemo(
    () =>
      fetched.map((row) => {
        const held = overrides[row.id];
        if (!held) return row;

        return {
          ...row,
          status: held.status ?? row.status,
          statusLabel: held.statusLabel ?? row.statusLabel,
          payment: held.payment ?? row.payment,
        };
      }),
    [overrides, fetched]
  );

  return {
    enquiries,
    count: query.isError ? 0 : query.data?.count ?? 0,
    status: query.isError
      ? "failed"
      : query.isFetching
      ? "loading"
      : "ready",
    error: query.isError ? failureMessage(query.error) : undefined,
    reload: () => {
      void query.refetch();
    },
  };
}
